import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  {
    label: "About",
    href: "/about",
    children: [
      { label: "About AOA Somalia", href: "/about" },
      { label: "Vision, Mission & Values", href: "/vision-mission" },
      { label: "Objectives", href: "/objectives" },
      { label: "Governance", href: "/governance" },
      { label: "Committees", href: "/committees" },
      { label: "Stakeholder Engagement", href: "/stakeholder-engagement" },
      { label: "Mandate Boundary", href: "/mandate" },
    ],
  },
  { label: "What We Do", href: "/what-we-do" },
  { label: "Membership", href: "/membership" },
  { label: "Resources", href: "/resources" },
  { label: "News", href: "/news" },
  { label: "Contact", href: "/contact" },
];

export default function Header({ settings }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(false);
  }, [location.pathname]);

  const mark = settings?.header_mark || "/logos/aoa_mark_navy.png";

  return (
    <header
      data-testid="site-header"
      className={[
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_2px_24px_-12px_rgba(10,31,68,0.25)]"
          : "bg-white",
      ].join(" ")}
    >
      <div className="aoa-container flex h-20 items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 md:gap-4 group min-w-0">
          <img
            src={mark}
            alt="AOA Somalia"
            width={170}
            height={64}
            className="h-14 w-auto md:h-16 transition-transform duration-300 group-hover:scale-105 flex-none"
            data-testid="header-logo-mark"
          />
          <span
            aria-hidden="true"
            className="hidden sm:block w-px h-12 md:h-14 bg-gradient-to-b from-transparent via-[hsl(var(--aoa-navy))]/25 to-transparent flex-none"
          />
          <div className="hidden sm:flex flex-col leading-[1.1] whitespace-nowrap min-w-0">
            <span className="text-[17px] md:text-[19px] font-extrabold tracking-tight text-[hsl(var(--aoa-navy))]">
              AOA Somalia
            </span>
            <span className="text-[10px] md:text-[11px] font-semibold tracking-[0.18em] uppercase text-[hsl(var(--aoa-royal))] mt-0.5">
              Airline Operators Association
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1" data-testid="primary-nav">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(false)}
              >
                <button
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-slate-700 hover:text-[hsl(var(--aoa-royal))] transition-colors"
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
                <AnimatePresence>
                  {openDropdown === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-0 top-full mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_24px_60px_-24px_rgba(10,31,68,0.35)]"
                    >
                      {item.children.map((c) => (
                        <NavLink
                          key={c.href}
                          to={c.href}
                          data-testid={`nav-sub-${c.href.replace("/", "")}`}
                          className={({ isActive }) =>
                            [
                              "block rounded-xl px-4 py-2.5 text-sm transition-colors",
                              isActive
                                ? "bg-[hsl(var(--aoa-gray-bg))] text-[hsl(var(--aoa-royal))] font-semibold"
                                : "text-slate-700 hover:bg-slate-50 hover:text-[hsl(var(--aoa-royal))]",
                            ].join(" ")
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  [
                    "whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "text-[hsl(var(--aoa-royal))]"
                      : "text-slate-700 hover:text-[hsl(var(--aoa-royal))]",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="ml-2 inline-flex items-center whitespace-nowrap rounded-full bg-[hsl(var(--aoa-navy))] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[hsl(var(--aoa-royal))] transition-colors"
          >
            Get in touch
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-[hsl(var(--aoa-navy))] flex-none"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="lg:hidden overflow-hidden border-t border-slate-200 bg-white"
            data-testid="mobile-drawer"
          >
            <div className="aoa-container py-4 flex flex-col gap-1">
              {NAV.flatMap((item) =>
                item.children
                  ? [
                      { label: item.label, href: item.href, group: true },
                      ...item.children.map((c) => ({ ...c, indent: true })),
                    ]
                  : [item],
              ).map((entry, idx) => (
                <NavLink
                  key={idx}
                  to={entry.href}
                  data-testid={`mobile-nav-${entry.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    [
                      "rounded-lg px-3 py-2.5 text-[15px]",
                      entry.indent ? "pl-6 text-slate-600" : "font-medium text-slate-800",
                      isActive ? "bg-slate-100 text-[hsl(var(--aoa-royal))]" : "",
                    ].join(" ")
                  }
                >
                  {entry.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
